import {WebMailPiecesJointes, WebMails} from "./models/webmail.model";

export type SelectedMailRef = {
    itemId: string;
    subject: string;
};

type MailItemLike = {
    itemId?: string;
    subject?: string;
    dateTimeCreated?: Date;
    itemType?: string;
    sender?: {displayName?: string; emailAddress?: string};
    from?: {displayName?: string; emailAddress?: string};
    to?: Array<{displayName?: string; emailAddress?: string}>;
    attachments?: Array<{
        id: string;
        name: string;
        contentType: string;
        attachmentType: string;
        size: number;
    }>;
    body?: {
        getAsync: (
            coercionType: Office.CoercionType,
            callback: (result: Office.AsyncResult<string>) => void
        ) => void;
    };
    getAttachmentContentAsync?: (
        attachmentId: string,
        callback: (result: Office.AsyncResult<Office.AttachmentContent>) => void
    ) => void;
    categories?: {
        addAsync: (
            categories: string[],
            callback: (result: Office.AsyncResult<void>) => void
        ) => void;
    };
    unloadAsync?: (callback: (result: Office.AsyncResult<void>) => void) => void;
};

/** Mailbox APIs from requirement set 1.15 not always present in @types/office-js */
type MailboxWithLoadItem = {
    loadItemByIdAsync?: (
        itemId: string,
        callback: (result: Office.AsyncResult<MailItemLike>) => void
    ) => void;
    getSelectedItemsAsync: Office.Mailbox["getSelectedItemsAsync"];
    item: Office.Mailbox["item"];
    addHandlerAsync: Office.Mailbox["addHandlerAsync"];
};

const getMailbox = (): MailboxWithLoadItem =>
    Office.context.mailbox as unknown as MailboxWithLoadItem;

export const isMultiSelectSupported = (): boolean => {
    try {
        return (
            Office.context.requirements.isSetSupported("Mailbox", "1.13") &&
            typeof Office.context.mailbox.getSelectedItemsAsync === "function"
        );
    } catch {
        return false;
    }
};

export const isLoadItemByIdSupported = (): boolean => {
    try {
        return (
            Office.context.requirements.isSetSupported("Mailbox", "1.15") &&
            typeof getMailbox().loadItemByIdAsync === "function"
        );
    } catch {
        return false;
    }
};

const refsFromCurrentItem = (): SelectedMailRef[] => {
    try {
        const item = Office.context.mailbox.item as MailItemLike | null;
        if (!item) {
            return [];
        }
        // itemId can throw in some hosts when unavailable
        const itemId = item.itemId;
        if (!itemId) {
            return [];
        }
        return [{itemId, subject: item.subject ?? ""}];
    } catch (error) {
        console.error("refsFromCurrentItem failed:", error);
        return [];
    }
};

const toMailRefs = (items: Array<{itemId?: string; subject?: string}> | undefined | null): SelectedMailRef[] =>
    (items ?? [])
        .filter((item) => !!item?.itemId)
        .map((item) => ({
            itemId: item.itemId as string,
            subject: item.subject ?? "",
        }));

/**
 * Returns selected message refs. Always falls back to the focused mailbox.item
 * when getSelectedItemsAsync is unavailable, fails, or returns an empty list.
 */
export const getSelectedMailRefs = (): Promise<SelectedMailRef[]> =>
    new Promise((resolve) => {
        const fallback = () => resolve(refsFromCurrentItem());

        if (!isMultiSelectSupported()) {
            fallback();
            return;
        }

        try {
            Office.context.mailbox.getSelectedItemsAsync((asyncResult) => {
                if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                    console.warn("getSelectedItemsAsync failed:", asyncResult.error?.message);
                    fallback();
                    return;
                }

                const refs = toMailRefs(asyncResult.value);
                resolve(refs.length > 0 ? refs : refsFromCurrentItem());
            });
        } catch (error) {
            console.warn("getSelectedItemsAsync threw:", error);
            fallback();
        }
    });

const getBodyHtml = (item: MailItemLike): Promise<string> =>
    new Promise((resolve) => {
        if (!item.body?.getAsync) {
            resolve("");
            return;
        }
        item.body.getAsync(Office.CoercionType.Html, (result) => {
            resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value : "");
        });
    });

const getAttachmentContent = (
    item: MailItemLike,
    attachmentId: string
): Promise<string> =>
    new Promise((resolve) => {
        if (!item.getAttachmentContentAsync) {
            resolve("");
            return;
        }
        item.getAttachmentContentAsync(attachmentId, (result) => {
            resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value.content : "");
        });
    });

const buildPiecesJointes = async (
    item: MailItemLike,
    selectedAttachmentNames?: string[]
): Promise<WebMailPiecesJointes[]> => {
    const attachments = item.attachments ?? [];
    const pieces: WebMailPiecesJointes[] = [];

    for (const attachment of attachments) {
        if (selectedAttachmentNames && !selectedAttachmentNames.includes(attachment.name)) {
            continue;
        }

        const content = await getAttachmentContent(item, attachment.id);
        if (!content) {
            continue;
        }

        const piece = new WebMailPiecesJointes();
        Object.assign(piece, {
            Nom: attachment.name,
            TypePieceJointe: attachment.contentType,
            TypeContenu: attachment.attachmentType,
            ContenuBase64: content,
            Taille: attachment.size,
        });
        pieces.push(piece);
    }

    return pieces;
};

const mapItemToWebMail = async (
    item: MailItemLike,
    options?: {selectedAttachmentNames?: string[]; includeAllAttachments?: boolean}
): Promise<WebMails> => {
    const webMail = new WebMails();
    const sender = item.sender ?? item.from;
    const to = item.to?.[0];

    Object.assign(webMail, {
        IdDossier: 0,
        ItemId: item.itemId,
        DeNom: sender?.displayName ?? "",
        DeEmail: sender?.emailAddress ?? "",
        DateHeure: item.dateTimeCreated
            ? new Date(item.dateTimeCreated).toISOString()
            : new Date().toISOString(),
        Sujet: item.subject ?? "",
        PJ: item.attachments?.length ?? 0,
        IsMsg: item.itemType === Office.MailboxEnums.ItemType.Message || item.itemType === "message",
        ANom: to?.displayName ?? "",
        AEmail: to?.emailAddress ?? "",
        IdTheme: 0,
        idCollaborateur: 2,
    });

    webMail.Body = await getBodyHtml(item);

    if (options?.includeAllAttachments) {
        webMail.WebMailPiecesJointes = await buildPiecesJointes(item);
    } else if (options?.selectedAttachmentNames) {
        webMail.WebMailPiecesJointes = await buildPiecesJointes(item, options.selectedAttachmentNames);
    } else {
        webMail.WebMailPiecesJointes = await buildPiecesJointes(item);
    }

    return webMail;
};

export const buildWebMailFromCurrentItem = async (
    selectedAttachmentNames?: string[]
): Promise<WebMails> => {
    const item = Office.context.mailbox.item as MailItemLike | null;
    if (!item) {
        throw new Error("Aucun email ouvert");
    }
    return mapItemToWebMail(item, {
        selectedAttachmentNames,
        includeAllAttachments: selectedAttachmentNames === undefined,
    });
};

const loadItemById = (itemId: string): Promise<MailItemLike> =>
    new Promise((resolve, reject) => {
        const mailbox = getMailbox();
        if (!mailbox.loadItemByIdAsync) {
            reject(new Error("loadItemByIdAsync non disponible"));
            return;
        }
        mailbox.loadItemByIdAsync(itemId, (result) => {
            if (result.status === Office.AsyncResultStatus.Failed) {
                reject(new Error(result.error.message));
                return;
            }
            resolve(result.value as MailItemLike);
        });
    });

const unloadItem = (item: MailItemLike): Promise<void> =>
    new Promise((resolve) => {
        if (!item.unloadAsync) {
            resolve();
            return;
        }
        item.unloadAsync((result) => {
            if (result.status === Office.AsyncResultStatus.Failed) {
                console.error("Failed to unload item:", result.error.message);
            }
            resolve();
        });
    });

export const buildWebMailFromItemId = async (
    itemId: string,
    options?: {selectedAttachmentNames?: string[]; includeAllAttachments?: boolean}
): Promise<WebMails> => {
    if (!isLoadItemByIdSupported()) {
        throw new Error("Le client Outlook ne prend pas en charge le chargement multi-mails (Mailbox 1.15 requis)");
    }

    const loadedItem = await loadItemById(itemId);
    try {
        return await mapItemToWebMail(loadedItem, {
            includeAllAttachments: options?.includeAllAttachments ?? true,
            selectedAttachmentNames: options?.selectedAttachmentNames,
        });
    } finally {
        await unloadItem(loadedItem);
    }
};

export const addCategoryToCurrentItem = (): Promise<void> =>
    new Promise((resolve) => {
        const item = Office.context.mailbox.item as MailItemLike | null;
        if (!item?.categories) {
            resolve();
            return;
        }
        item.categories.addAsync(["zLawyer"], (result) => {
            if (result.status === Office.AsyncResultStatus.Failed) {
                console.error("Failed to add category:", result.error.message);
            }
            resolve();
        });
    });

export const addCategoryToItemId = async (itemId: string): Promise<void> => {
    if (!isLoadItemByIdSupported()) {
        return;
    }

    const loadedItem = await loadItemById(itemId);
    try {
        if (loadedItem.categories) {
            await new Promise<void>((resolve) => {
                loadedItem.categories.addAsync(["zLawyer"], (result) => {
                    if (result.status === Office.AsyncResultStatus.Failed) {
                        console.error("Failed to add category:", result.error.message);
                    }
                    resolve();
                });
            });
        }
    } finally {
        await unloadItem(loadedItem);
    }
};

export const registerSelectedItemsChanged = (handler: () => void): (() => void) => {
    if (!isMultiSelectSupported()) {
        return () => undefined;
    }

    let debounceTimer: ReturnType<typeof setTimeout> | undefined;
    const debouncedHandler = () => {
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }
        debounceTimer = setTimeout(() => handler(), 250);
    };

    Office.context.mailbox.addHandlerAsync(Office.EventType.SelectedItemsChanged, debouncedHandler, (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
            console.error("Failed to register SelectedItemsChanged:", asyncResult.error.message);
        }
    });

    return () => {
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }
        try {
            Office.context.mailbox.removeHandlerAsync(Office.EventType.SelectedItemsChanged, () => undefined);
        } catch {
            // ignore
        }
    };
};
